import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  useAdminTenants,
  useCreateSubscription,
  useUpdateSubscription,
  useDeleteSubscription,
  useConfirmPayment,
  useBillingSettings,
  useUpdateBillingSettings,
} from '@/hooks/useAdmin';
import { TenantWithSubscription, SubscriptionCreate, SubscriptionUpdate } from '@/services/admin';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import {
  CreditCard,
  Search,
  Plus,
  Edit2,
  Building2,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  Trash2,
  BellRing,
  Banknote,
  Save,
} from 'lucide-react';

// Helpers para convertir datetime ISO <-> input[type=date] (YYYY-MM-DD).
// Mandamos a mediodia para evitar que la zona horaria mueva el dia
// (Argentina = GMT-3: T00:00:00 UTC se interpreta como 21h del dia anterior).
const toDateInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const fromDateInput = (value: string): string | null => {
  if (!value) return null;
  return `${value}T12:00:00`;
};

export default function AdminSubscriptions() {
  const { data: tenants, isLoading } = useAdminTenants();
  const createSubscription = useCreateSubscription();
  const updateSubscription = useUpdateSubscription();
  const deleteSubscription = useDeleteSubscription();
  const confirmPayment = useConfirmPayment();
  const { data: billingSettings } = useBillingSettings();
  const updateBillingSettings = useUpdateBillingSettings();

  // Datos de cobro de la plataforma
  const [billingForm, setBillingForm] = useState({
    payment_alias: '',
    cbu: '',
    cvu: '',
    bank_holder: '',
    whatsapp_number: '',
    payment_due_day: 15,
  });

  useEffect(() => {
    if (billingSettings) {
      setBillingForm({
        payment_alias: billingSettings.payment_alias || '',
        cbu: billingSettings.cbu || '',
        cvu: billingSettings.cvu || '',
        bank_holder: billingSettings.bank_holder || '',
        whatsapp_number: billingSettings.whatsapp_number || '',
        payment_due_day: billingSettings.payment_due_day ?? 15,
      });
    }
  }, [billingSettings]);

  const handleSaveBilling = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateBillingSettings.mutateAsync(billingForm);
      toast.success('Datos de cobro guardados');
    } catch (error: any) {
      toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleConfirmPayment = async (tenant: TenantWithSubscription) => {
    if (!tenant.subscription) return;
    const confirmed = window.confirm(
      `¿Confirmar que ${tenant.name} abonó? Se marcará como pagado y el próximo vencimiento pasará a +1 mes.`
    );
    if (!confirmed) return;
    try {
      await confirmPayment.mutateAsync({ id: tenant.subscription.id });
      toast.success('Pago confirmado');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Error al confirmar el pago');
    }
  };

  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantWithSubscription | null>(null);
  const [formData, setFormData] = useState<{
    plan: string;
    status: string;
    price_monthly: number;
    payment_status: string;
    start_date: string;
    next_payment_date: string;
    end_date: string;
    notes: string;
    ipc_adjustment_enabled: boolean;
  }>({
    plan: 'basic',
    status: 'active',
    price_monthly: 40000,
    payment_status: 'pending',
    start_date: toDateInput(new Date().toISOString()),
    next_payment_date: '',
    end_date: '',
    notes: '',
    ipc_adjustment_enabled: true,
  });

  const tenantsWithSub = tenants?.filter((t) => t.subscription);
  const tenantsWithoutSub = tenants?.filter((t) => !t.subscription);

  const filteredTenants = tenants?.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(search.toLowerCase()) ||
      tenant.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenCreate = (tenant: TenantWithSubscription) => {
    setEditingTenant(tenant);
    setFormData({
      plan: 'basic',
      status: 'active',
      price_monthly: 40000,
      payment_status: 'pending',
      start_date: toDateInput(new Date().toISOString()),
      next_payment_date: '',
      end_date: '',
      notes: '',
      ipc_adjustment_enabled: true,
    });
    setShowModal(true);
  };

  const handleOpenEdit = (tenant: TenantWithSubscription) => {
    setEditingTenant(tenant);
    if (tenant.subscription) {
      setFormData({
        plan: tenant.subscription.plan,
        status: tenant.subscription.status,
        price_monthly: Number(tenant.subscription.price_monthly),
        payment_status: tenant.subscription.payment_status,
        start_date: toDateInput(tenant.subscription.start_date),
        next_payment_date: tenant.subscription.next_payment_date
          ? toDateInput(tenant.subscription.next_payment_date)
          : '',
        end_date: tenant.subscription.end_date ? toDateInput(tenant.subscription.end_date) : '',
        notes: tenant.subscription.notes || '',
        ipc_adjustment_enabled: tenant.subscription.ipc_adjustment_enabled ?? true,
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenant) return;

    const payload = {
      plan: formData.plan,
      status: formData.status,
      price_monthly: formData.price_monthly,
      payment_status: formData.payment_status,
      start_date: fromDateInput(formData.start_date),
      next_payment_date: formData.next_payment_date
        ? fromDateInput(formData.next_payment_date)
        : null,
      // Solo tiene sentido en una prueba: es cuando se termina.
      end_date: formData.end_date ? fromDateInput(formData.end_date) : null,
      notes: formData.notes,
      ipc_adjustment_enabled: formData.ipc_adjustment_enabled,
    };

    try {
      if (editingTenant.subscription) {
        await updateSubscription.mutateAsync({
          id: editingTenant.subscription.id,
          data: payload as SubscriptionUpdate,
        });
        toast.success('Suscripción actualizada');
      } else {
        await createSubscription.mutateAsync({
          tenant_id: editingTenant.id,
          ...payload,
        } as SubscriptionCreate);
        toast.success('Suscripción creada');
      }
      setShowModal(false);
      setEditingTenant(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Error al guardar la suscripción');
    }
  };

  const handleDelete = async () => {
    if (!editingTenant?.subscription) return;
    const confirmed = window.confirm(
      `¿Eliminar la suscripción de ${editingTenant.name}? Esta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    try {
      await deleteSubscription.mutateAsync(editingTenant.subscription.id);
      toast.success('Suscripción eliminada');
      setShowModal(false);
      setEditingTenant(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la suscripción');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    // Parsear la parte de fecha del string sin convertirla a Date para evitar
    // que el navegador la mueva por zona horaria.
    const datePart = dateString.slice(0, 10); // "YYYY-MM-DD"
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return dateString;
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${String(d).padStart(2, '0')} de ${months[m - 1]} de ${y}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Activa</span>;
      case 'trial':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Prueba</span>;
      case 'suspended':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Suspendida</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Cancelada</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">{status}</span>;
    }
  };

  const getPaymentBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">Pagado</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-1 text-amber-600">
            <Clock className="h-4 w-4" />
            <span className="text-sm">Pendiente</span>
          </div>
        );
      case 'overdue':
        return (
          <div className="flex items-center gap-1 text-red-600">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Vencido</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suscripciones</h1>
          <p className="text-gray-500">Gestiona las suscripciones de las veterinarias</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{tenantsWithSub?.length || 0}</p>
              <p className="text-xs text-gray-500">Con suscripción</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{tenantsWithoutSub?.length || 0}</p>
              <p className="text-xs text-gray-500">Sin suscripción</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tenantsWithSub?.filter((t) => t.subscription?.payment_status === 'paid').length || 0}
              </p>
              <p className="text-xs text-gray-500">Al día</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tenantsWithSub?.filter((t) => t.subscription?.payment_status === 'overdue').length || 0}
              </p>
              <p className="text-xs text-gray-500">Vencidos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Datos de cobro de la plataforma */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Banknote className="h-5 w-5 text-indigo-600" />
          <h2 className="font-semibold text-gray-900">Datos de cobro</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Lo que ven las veterinarias para transferir y avisar el pago. Cargá al menos uno de alias/CBU/CVU y el WhatsApp.
        </p>
        <form onSubmit={handleSaveBilling} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alias</label>
            <Input
              value={billingForm.payment_alias}
              onChange={(e) => setBillingForm({ ...billingForm, payment_alias: e.target.value })}
              placeholder="mi.alias.mp"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titular (opcional)</label>
            <Input
              value={billingForm.bank_holder}
              onChange={(e) => setBillingForm({ ...billingForm, bank_holder: e.target.value })}
              placeholder="Nombre del titular"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CBU</label>
            <Input
              value={billingForm.cbu}
              onChange={(e) => setBillingForm({ ...billingForm, cbu: e.target.value })}
              placeholder="22 dígitos"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CVU</label>
            <Input
              value={billingForm.cvu}
              onChange={(e) => setBillingForm({ ...billingForm, cvu: e.target.value })}
              placeholder="Billetera virtual (22 dígitos)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp para avisos <span className="text-red-500">*</span>
            </label>
            <Input
              value={billingForm.whatsapp_number}
              onChange={(e) => setBillingForm({ ...billingForm, whatsapp_number: e.target.value })}
              placeholder="5493815791046 (código país + número)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Se puede abonar hasta el día
            </label>
            <Input
              type="number"
              min={1}
              max={28}
              value={billingForm.payment_due_day}
              onChange={(e) =>
                setBillingForm({ ...billingForm, payment_due_day: Number(e.target.value) || 15 })
              }
            />
            <p className="text-xs text-gray-500 mt-1">
              Vale para todas las veterinarias: al confirmar un pago, el próximo vencimiento
              cae ese día del mes siguiente. Hasta 28, para que exista también en febrero.
            </p>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" loading={updateBillingSettings.isPending} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Guardar datos de cobro
            </Button>
          </div>
        </form>
      </div>

      {/* Tenants without subscription */}
      {tenantsWithoutSub && tenantsWithoutSub.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <h3 className="font-semibold text-amber-900 mb-3">Veterinarias sin suscripción</h3>
          <div className="flex flex-wrap gap-2">
            {tenantsWithoutSub.map((tenant) => (
              <button
                key={tenant.id}
                onClick={() => handleOpenCreate(tenant)}
                className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors"
              >
                <Building2 className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">{tenant.name}</span>
                <Plus className="h-4 w-4 text-amber-600" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Subscriptions List */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Suscripciones Activas</h2>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Cargando...</div>
        ) : filteredTenants && filteredTenants.filter((t) => t.subscription).length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredTenants
              .filter((t) => t.subscription)
              .map((tenant) => (
                <div
                  key={tenant.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(tenant.subscription!.status)}
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full capitalize">
                              Plan {tenant.subscription!.plan}
                            </span>
                            {/* El precio se revisa cada 3 meses: solo se avisa, no se
                                aplica nada solo. */}
                            {tenant.subscription!.is_adjustment_month && (
                              <span
                                className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium"
                                title={`Hace ${tenant.subscription!.months_since_price_change} meses que no cambia el precio`}
                              >
                                Mes de ajuste
                              </span>
                            )}
                            {tenant.subscription!.ipc_adjustment_enabled === false && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                                Sin ajuste por IPC
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(Number(tenant.subscription!.price_monthly))}
                          </p>
                          <p className="text-sm text-gray-500">/mes</p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
                        {getPaymentBadge(tenant.subscription!.payment_status)}
                        {tenant.subscription!.payment_reported_at && (
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                            <BellRing className="h-4 w-4" />
                            <span className="text-xs font-medium">
                              Pago informado {formatDate(tenant.subscription!.payment_reported_at)}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>Desde {formatDate(tenant.subscription!.start_date)}</span>
                        </div>
                        {tenant.subscription!.next_payment_date && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                            <span>Próximo pago: {formatDate(tenant.subscription!.next_payment_date)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleOpenEdit(tenant)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                        title="Editar suscripción"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleConfirmPayment(tenant)}
                        disabled={confirmPayment.isPending}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                          tenant.subscription!.payment_reported_at
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title="Marcar pago recibido y avanzar el vencimiento"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Confirmar pago
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No hay suscripciones</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTenant(null);
        }}
        title={editingTenant?.subscription ? 'Editar Suscripción' : 'Nueva Suscripción'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Veterinaria</p>
            <p className="font-semibold text-gray-900">{editingTenant?.name}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="basic">Básico</option>
                <option value="professional">Profesional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="trial">Prueba</option>
                <option value="active">Activa</option>
                <option value="suspended">Suspendida</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Precio Mensual (ARS)"
              type="number"
              value={formData.price_monthly}
              onChange={(e) => setFormData({ ...formData, price_monthly: Number(e.target.value) })}
              min={0}
              step={100}
              placeholder="Ej: 40000"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado de Pago</label>
              <select
                value={formData.payment_status}
                onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="pending">Pendiente</option>
                <option value="paid">Pagado</option>
                <option value="overdue">Vencido</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Fecha de inicio"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
            {formData.status === 'trial' && (
              <div>
                <Input
                  label="Fin de la prueba"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Es la fecha que ve el cliente en su panel. Sin esto no se le muestra cuánto le queda.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Próximo vencimiento"
                type="date"
                value={formData.next_payment_date}
                onChange={(e) => setFormData({ ...formData, next_payment_date: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Hasta cuándo puede abonar. El cliente ve el recordatorio en su panel desde el
                1° hasta ese día. Al confirmar un pago se corre solo al mes siguiente.
              </p>
            </div>
            <div className="flex items-start pt-7">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.ipc_adjustment_enabled}
                  onChange={(e) =>
                    setFormData({ ...formData, ipc_adjustment_enabled: e.target.checked })
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700">Ajusta por IPC</span>
                  <span className="block text-xs text-gray-500">
                    Avisa cada 3 meses que corresponde revisar el precio, acá y en el panel del
                    cliente. No cambia ningún monto: eso lo cargás vos.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Notas adicionales..."
            />
          </div>

          <div className="flex justify-between items-center gap-3 pt-4 border-t">
            <div>
              {editingTenant?.subscription && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDelete}
                  loading={deleteSubscription.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Eliminar
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowModal(false);
                  setEditingTenant(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={createSubscription.isPending || updateSubscription.isPending}
              >
                {editingTenant?.subscription ? 'Guardar Cambios' : 'Crear Suscripción'}
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
